/**
 * Pure helpers for the Work Orders page (tracker item 58): the selection
 * (kept across filters and pages), copy counts, the print order preview,
 * the sheets to print, and server problem lists. Nothing here changes a Job.
 */

import axios from 'axios';
import * as types from '../api/types';

/** One selected job and its requested copies. */
export interface SelectedWorkOrder {
  candidate: types.WorkOrderCandidate;
  serviceCopies: number;
  billingCopies: number;
}

/** Selection keyed by job id; insertion order is irrelevant (the print order is the server's). */
export type WorkOrderSelectionMap = ReadonlyMap<string, SelectedWorkOrder>;

/** Initial copies per job (decision 5): one service copy and one billing copy. */
export const DEFAULT_SERVICE_COPIES = 1;
export const DEFAULT_BILLING_COPIES = 1;

/** Add candidates; jobs already selected keep their copy counts. Returns the new map and how many were added. */
export function addToSelection(
  selection: WorkOrderSelectionMap,
  candidates: types.WorkOrderCandidate[],
): { selection: Map<string, SelectedWorkOrder>; added: number } {
  const next = new Map(selection);
  let added = 0;
  for (const candidate of candidates) {
    const existing = next.get(candidate.id);
    if (existing) {
      next.set(candidate.id, { ...existing, candidate });
    } else {
      next.set(candidate.id, { candidate, serviceCopies: DEFAULT_SERVICE_COPIES, billingCopies: DEFAULT_BILLING_COPIES });
      added += 1;
    }
  }
  return { selection: next, added };
}

export function removeFromSelection(selection: WorkOrderSelectionMap, jobIds: string[]): Map<string, SelectedWorkOrder> {
  const next = new Map(selection);
  jobIds.forEach((id) => next.delete(id));
  return next;
}

/**
 * Set one copy count from an input value. Blank or out-of-range input is kept
 * as typed (NaN for blank) so the field shows its error rather than silently
 * becoming another number.
 */
export function setCopies(
  selection: WorkOrderSelectionMap,
  jobId: string,
  type: types.WorkOrderCopyType,
  raw: string,
): Map<string, SelectedWorkOrder> {
  const next = new Map(selection);
  const entry = next.get(jobId);
  if (!entry) return next;
  const value = raw.trim() === '' ? Number.NaN : Number(raw);
  next.set(jobId, type === 'SERVICE' ? { ...entry, serviceCopies: value } : { ...entry, billingCopies: value });
  return next;
}

export function isValidCopyCount(value: number): boolean {
  return Number.isInteger(value) && value >= 0 && value <= types.MAX_WORK_ORDER_COPIES;
}

/** Why this job cannot be prepared as it stands, or null. */
export function selectionEntryError(entry: SelectedWorkOrder): string | null {
  if (!entry.candidate.hasWorkItems) return 'No work items: add at least one before printing, or remove this job.';
  if (!isValidCopyCount(entry.serviceCopies) || !isValidCopyCount(entry.billingCopies)) {
    return `Copies must be whole numbers from 0 to ${types.MAX_WORK_ORDER_COPIES}.`;
  }
  if (entry.serviceCopies + entry.billingCopies < 1) return 'Print at least one service or billing copy.';
  return null;
}

export interface SelectionTotals {
  jobs: number;
  serviceCopies: number;
  billingCopies: number;
  invalid: number;
}

export function selectionTotals(selection: WorkOrderSelectionMap): SelectionTotals {
  let serviceCopies = 0;
  let billingCopies = 0;
  let invalid = 0;
  selection.forEach((entry) => {
    if (selectionEntryError(entry)) invalid += 1;
    if (isValidCopyCount(entry.serviceCopies)) serviceCopies += entry.serviceCopies;
    if (isValidCopyCount(entry.billingCopies)) billingCopies += entry.billingCopies;
  });
  return { jobs: selection.size, serviceCopies, billingCopies, invalid };
}

/** The request body for POST /work-order-batches. */
export function prepareRequest(date: string, selection: WorkOrderSelectionMap): types.PrepareWorkOrderBatchRequest {
  return {
    date,
    jobs: Array.from(selection.values()).map((entry) => ({
      id: entry.candidate.id,
      serviceCopies: entry.serviceCopies,
      billingCopies: entry.billingCopies,
    })),
  };
}

const UNROUTED_LABEL = 'Unrouted';
const UNASSIGNED_LABEL = 'Unassigned';
const NO_TIME_LABEL = 'No time';

function fold(value: string): string {
  return value.toLowerCase();
}

function compareText(a: string, b: string): number {
  const folded = fold(a).localeCompare(fold(b), 'en');
  if (folded !== 0) return folded;
  return a < b ? -1 : a > b ? 1 : 0;
}

function compareNullable(a: string | null, b: string | null): number {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return compareText(a, b);
}

function compareGroup(aLabel: string | null, aId: string | null, bLabel: string | null, bId: string | null): number {
  const aKey = aId === null ? null : aLabel ?? aId;
  const bKey = bId === null ? null : bLabel ?? bId;
  return compareNullable(aKey, bKey) || compareNullable(aId, bId);
}

/**
 * Print order preview, the same rule as the server (which decides the stored
 * order): Route, then Technician, then start time, then job id; Unrouted,
 * Unassigned and No time last.
 */
export function compareCandidates(a: types.WorkOrderCandidate, b: types.WorkOrderCandidate): number {
  return (
    compareGroup(a.routeName, a.routeId, b.routeName, b.routeId) ||
    compareGroup(a.assignedToEmail, a.assignedTo, b.assignedToEmail, b.assignedTo) ||
    compareNullable(a.scheduledStartTime, b.scheduledStartTime) ||
    compareText(a.id, b.id)
  );
}

export function routeLabel(routeName: string | null | undefined): string {
  return routeName || UNROUTED_LABEL;
}

export function technicianLabel(email: string | null | undefined): string {
  return email || UNASSIGNED_LABEL;
}

export function timeLabel(time: string | null | undefined): string {
  return time || NO_TIME_LABEL;
}

export interface WorkOrderGroup<T> {
  key: string;
  route: string;
  technician: string;
  items: T[];
}

/** Consecutive Route + Technician groups of an already ordered list (for headings). */
export function groupByRouteAndTechnician<T>(
  rows: T[],
  keyOf: (row: T) => { routeId: string | null; routeName: string | null; assignedTo: string | null; assignedToEmail: string | null },
): WorkOrderGroup<T>[] {
  const groups: WorkOrderGroup<T>[] = [];
  for (const row of rows) {
    const k = keyOf(row);
    const key = `${k.routeId ?? types.WORK_ORDER_UNROUTED}|${k.assignedTo ?? types.WORK_ORDER_UNASSIGNED}`;
    const last = groups[groups.length - 1];
    if (last && last.key === key) {
      last.items.push(row);
    } else {
      groups.push({ key, route: routeLabel(k.routeName), technician: technicianLabel(k.assignedToEmail), items: [row] });
    }
  }
  return groups;
}

/** Group key of a batch member (its Job's current route and assignee). */
export function memberGroupKey(member: types.WorkOrderBatchMember) {
  return {
    routeId: member.job?.routeId ?? null,
    routeName: member.routeName,
    assignedTo: member.job?.assignedTo ?? null,
    assignedToEmail: member.job?.assignedToUser?.email ?? null,
  };
}

/** One printed copy: its own fresh sheet. */
export interface WorkOrderSheet {
  key: string;
  member: types.WorkOrderBatchMember;
  job: types.Job;
  copy: types.WorkOrderCopyType;
  /** 1-based copy number within its type */
  index: number;
  of: number;
}

/**
 * Every copy to print for the members in this print run, in the batch order:
 * per job its service copies, then its billing copies. Members whose Job is
 * missing are skipped here; printBlockers reports them.
 */
export function expandSheets(members: types.WorkOrderBatchMember[], included: ReadonlySet<string>): WorkOrderSheet[] {
  const sheets: WorkOrderSheet[] = [];
  for (const member of members) {
    if (!included.has(member.jobId) || !member.job) continue;
    const add = (copy: types.WorkOrderCopyType, count: number) => {
      for (let index = 1; index <= count; index += 1) {
        sheets.push({ key: `${member.jobId}:${copy}:${index}`, member, job: member.job!, copy, index, of: count });
      }
    };
    add('SERVICE', member.serviceCopies);
    add('BILLING', member.billingCopies);
  }
  return sheets;
}

/** The members a print run starts with: unconfirmed ones that can print, or every printable one once all are confirmed. */
export function defaultPrintRun(members: types.WorkOrderBatchMember[]): Set<string> {
  const printable = members.filter((m) => m.job);
  const unconfirmed = printable.filter((m) => !m.confirmedAt);
  return new Set((unconfirmed.length > 0 ? unconfirmed : printable).map((m) => m.jobId));
}

/** Members in the print run that cannot be printed (missing Job): printing is blocked until they are excluded. */
export function printBlockers(members: types.WorkOrderBatchMember[], included: ReadonlySet<string>): types.WorkOrderBatchMember[] {
  return members.filter((m) => included.has(m.jobId) && !m.job);
}

/** The version each printed document showed, captured when Print was clicked. */
export function printedVersions(members: types.WorkOrderBatchMember[], included: ReadonlySet<string>): Map<string, string> {
  const versions = new Map<string, string>();
  members.forEach((m) => {
    if (included.has(m.jobId) && m.job) versions.set(m.jobId, m.job.updatedAt);
  });
  return versions;
}

/** Members of a print run that may still be confirmed (unconfirmed, no review needed). */
export function confirmableInRun(members: types.WorkOrderBatchMember[], run: ReadonlyMap<string, string>): types.WorkOrderBatchMember[] {
  return members.filter((m) => run.has(m.jobId) && !m.confirmedAt && !m.confirmBlock);
}

/** POST /:id/confirm body for the jobs staff ticked. */
export function confirmRequest(ticked: string[], run: ReadonlyMap<string, string>): Array<{ id: string; printedVersion: string }> {
  return ticked.filter((id) => run.has(id)).map((id) => ({ id, printedVersion: run.get(id)! }));
}

/** The per-job problems the server sent with a 409 / 400, if any. */
export function problemsFromError(err: unknown): types.WorkOrderJobProblem[] {
  if (!axios.isAxiosError(err)) return [];
  const data = err.response?.data as { details?: { problems?: unknown } } | undefined;
  const problems = data?.details?.problems;
  if (!Array.isArray(problems)) return [];
  return problems.filter((p): p is types.WorkOrderJobProblem => !!p && typeof p === 'object' && typeof (p as { jobId?: unknown }).jobId === 'string');
}

export function jobLabel(job: Pick<types.Job, 'customer' | 'property'> | null | undefined, fallbackId: string): string {
  if (!job) return `Job ${fallbackId}`;
  return [job.customer?.name, job.property?.address].filter(Boolean).join(' - ') || `Job ${fallbackId}`;
}

export function batchStatusLabel(status: types.WorkOrderBatchStatus): string {
  if (status === 'PARTIALLY_CONFIRMED') return 'Partially confirmed';
  if (status === 'CONFIRMED') return 'Confirmed';
  return 'Prepared';
}

const CONFIRM_BLOCK_MESSAGES: Record<types.WorkOrderConfirmBlock, string> = {
  JOB_MISSING: 'This job no longer exists. Exclude it from the print run.',
  STATUS_CHANGED: 'Its status changed since preparation. Review the job; it cannot be confirmed from this batch.',
  DATE_CHANGED: 'It was rescheduled to another day. Review the job; it cannot be confirmed from this batch.',
};

export function confirmBlockMessage(block: types.WorkOrderConfirmBlock): string {
  return CONFIRM_BLOCK_MESSAGES[block];
}

/** Plain-language summary of a confirmation result. */
export function confirmationSummary(result: types.ConfirmWorkOrderJobsResult): string {
  const parts = [
    `${result.newlyGenerated.length} moved to Work Order Generated`,
    `${result.alreadyGenerated.length} already generated`,
    `${result.stillUnconfirmed.length} still unconfirmed`,
  ];
  if (result.alreadyConfirmed.length > 0) parts.push(`${result.alreadyConfirmed.length} were already confirmed`);
  return `${parts.join(', ')}.`;
}

export function pluralize(count: number, noun: string, plural = `${noun}s`): string {
  return `${count} ${count === 1 ? noun : plural}`;
}
